import { SetStateAction, useCallback, useState } from 'react';

type StitchedState<T> = { source?: T; value?: T };

/**
 * Stitches each new page of a query result into the pages before it.
 * The merge runs during render when `data` changes, so the stitched value is never a render behind the data it came
 * from (no intermediate "stitching" commit). `stitch` must be pure: StrictMode may call it twice.
 * @returns The stitched value and a setter for optimistic updates or resets
 */
const useStitchedResult = <T>(data: T | undefined, stitch: (previous: T | undefined, next: T) => T) => {
  const [stitched, setStitched] = useState<StitchedState<T>>(() => ({
    source: data,
    value: data ? stitch(undefined, data) : undefined,
  }));

  let current = stitched;
  if (data && data !== stitched.source) {
    current = { source: data, value: stitch(stitched.value, data) };
    setStitched(current);
  }

  const setValue = useCallback((update: SetStateAction<T | undefined>) => {
    setStitched((previous) => ({
      ...previous,
      value: update instanceof Function ? update(previous.value) : update,
    }));
  }, []);

  return [current.value, setValue] as const;
};

export default useStitchedResult;
